import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Search } from "lucide-react";
import { NAV } from "@/lib/content";

export function Wordmark({ compact = false, light = false }) {
  return (
    <Link to="/" data-testid="logo-home-link" className="group flex flex-col leading-none">
      <span className={`font-display font-extrabold tracking-tight transition-colors ${compact ? "text-xl" : "text-2xl"} ${light ? "text-white" : "text-navy"}`}>
        DBRF
      </span>
      {!compact && (
        <span className={`font-mono text-[0.6rem] uppercase tracking-[0.2em] transition-colors ${light ? "text-white/55" : "text-[var(--mineral)]"}`}>
          Digital Brain Research Foundation
        </span>
      )}
    </Link>
  );
}

export function Header({ onOpenSearch }) {
  const [scrolled, setScrolled] = useState(false);
  const [dark, setDark] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  // Track scroll position + whether a dark full-bleed section (data-nav-dark)
  // currently sits behind the header band, so the header can flip to light text.
  useEffect(() => {
    const compute = () => {
      setScrolled(window.scrollY > 24);
      const mid = 34; // sample point at the header's vertical midline
      let over = false;
      document.querySelectorAll("[data-nav-dark]").forEach((n) => {
        const r = n.getBoundingClientRect();
        if (r.top <= mid && r.bottom >= mid) over = true;
      });
      setDark(over);
    };
    compute();
    const raf = requestAnimationFrame(compute);
    const t = setTimeout(compute, 150);
    window.addEventListener("scroll", compute, { passive: true });
    window.addEventListener("resize", compute);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
      window.removeEventListener("scroll", compute);
      window.removeEventListener("resize", compute);
    };
  }, [loc.pathname]);

  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const light = dark; // dark background => light-coloured header content
  const headerBg = dark
    ? "border-white/10 bg-[var(--navy)]/85 backdrop-blur-md"
    : scrolled
      ? "border-[var(--line)] bg-[var(--paper)]/90 backdrop-blur-md"
      : "border-transparent bg-[var(--paper)]";

  const iconBtn = light
    ? "border-white/30 text-white hover:border-white"
    : "border-[var(--line)] text-navy hover:border-[var(--cobalt)] hover:text-[var(--cobalt)]";
  const outlineBtn = light
    ? "border-white/40 text-white hover:border-white"
    : "border-[var(--line)] text-navy hover:border-[var(--cobalt)] hover:text-[var(--cobalt)]";
  const primaryBtn = light
    ? "bg-[var(--cyan)] text-[var(--navy)] hover:bg-white"
    : "bg-[var(--navy)] text-white hover:bg-[var(--cobalt)]";

  return (
    <header className={`sticky top-0 z-40 border-b transition-all duration-200 ${headerBg}`}>
      <div className="container-dbrf flex items-center justify-between" style={{ height: scrolled ? 64 : 80 }}>
        <Wordmark compact={scrolled} light={light} />

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.to.slice(1)}`}
              className={({ isActive }) =>
                `link-underline text-sm font-medium transition-colors ${
                  isActive
                    ? light ? "text-[var(--cyan)]" : "text-[var(--cobalt)]"
                    : light ? "text-white/85 hover:text-white" : "text-navy"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <button
            onClick={onOpenSearch}
            data-testid="open-search-btn"
            aria-label="Open search"
            className={`btn-transition flex h-10 w-10 items-center justify-center rounded-full border transition-colors ${iconBtn}`}
          >
            <Search size={18} />
          </button>
          <Link
            to="/join-the-network"
            data-testid="header-join-btn"
            className={`btn-transition rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${outlineBtn}`}
          >
            Join the network
          </Link>
          <Link
            to="/bring-a-challenge"
            data-testid="header-challenge-btn"
            className={`btn-transition rounded-full px-4 py-2 text-sm font-semibold transition-colors ${primaryBtn}`}
          >
            Bring us a challenge
          </Link>
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <button onClick={onOpenSearch} data-testid="open-search-btn-mobile" aria-label="Open search" className={`flex h-10 w-10 items-center justify-center rounded-full border transition-colors ${iconBtn}`}>
            <Search size={18} />
          </button>
          <button
            onClick={() => setOpen(true)}
            data-testid="mobile-menu-open"
            aria-label="Open menu"
            aria-expanded={open}
            className={`flex h-10 w-10 items-center justify-center rounded-full border transition-colors ${iconBtn}`}
          >
            <Menu size={20} />
          </button>
        </div>
      </div>

      {open && createPortal(
        <div className="fixed inset-0 z-[60] overflow-y-auto bg-[var(--paper)] lg:hidden" role="dialog" aria-modal="true" data-testid="mobile-drawer">
          <div className="container-dbrf flex h-20 items-center justify-between">
            <Wordmark />
            <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" aria-label="Close menu" className="flex h-10 w-10 items-center justify-center rounded-full border border-[var(--line)] text-navy">
              <X size={20} />
            </button>
          </div>
          <nav className="container-dbrf flex flex-col gap-1 pt-4" aria-label="Mobile">
            {NAV.map((item) => (
              <NavLink key={item.to} to={item.to} data-testid={`mobile-nav-${item.to.slice(1)}`} className="border-b border-[var(--line)] py-4 font-display text-2xl text-navy">
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="container-dbrf mt-6 flex flex-col gap-3 pb-10">
            <Link to="/bring-a-challenge" data-testid="mobile-challenge-btn" className="rounded-full bg-[var(--navy)] px-5 py-3 text-center font-semibold text-white">
              Bring us a challenge
            </Link>
            <Link to="/join-the-network" data-testid="mobile-join-btn" className="rounded-full border border-[var(--line)] px-5 py-3 text-center font-semibold text-navy">
              Join the network
            </Link>
          </div>
        </div>,
        document.body
      )}
    </header>
  );
}
